/**
 * EditEntryModal - Create or edit a cashflow entry manually
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  X,
  Save,
  Trash2,
  Calendar,
  DollarSign,
  Tag,
  Building2,
  RotateCcw,
  FileText,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Category options
const CATEGORIES = [
  { value: 'salary', label: 'Maaş' },
  { value: 'rent', label: 'Kira' },
  { value: 'credit_card', label: 'Kredi Kartı' },
  { value: 'loan', label: 'Kredi/Taksit' },
  { value: 'subscription', label: 'Abonelik' },
  { value: 'bill', label: 'Fatura' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'investment', label: 'Yatırım' },
  { value: 'shopping', label: 'Alışveriş' },
  { value: 'food', label: 'Yemek' },
  { value: 'transport', label: 'Ulaşım' },
  { value: 'health', label: 'Sağlık' },
  { value: 'entertainment', label: 'Eğlence' },
  { value: 'other', label: 'Diğer' },
];

// Currency options
const CURRENCIES = [
  { value: 'TRY', label: '₺ TRY', symbol: '₺' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' },
  { value: 'GBP', label: '£ GBP', symbol: '£' },
];

// Account type options
const ACCOUNT_TYPES = [
  { value: 'bank', label: 'Banka Hesabı' },
  { value: 'credit_card', label: 'Kredi Kartı' },
  { value: 'cash', label: 'Nakit' },
  { value: 'crypto', label: 'Kripto' },
  { value: 'other', label: 'Diğer' },
];

// Recurring frequency options
const FREQUENCIES = [
  { value: 'weekly', label: 'Haftalık' },
  { value: 'biweekly', label: '2 Haftada Bir' },
  { value: 'monthly', label: 'Aylık' },
  { value: 'yearly', label: 'Yıllık' },
];

const EditEntryModal = () => {
  const {
    selectedEntry,
    closeEditModal,
    createEntry,
    updateEntry,
    deleteEntry,
    deleteLoanGroup,
    updateLoanGroupSharedFields,
    currentMonth,
  } = useCashflowStore();

  const isEditing = !!selectedEntry?.id && !selectedEntry.is_virtual;
  const isLoanInstallment = !!selectedEntry?.loan_group_id;
  const installmentInfo = isLoanInstallment
    ? `Taksit ${selectedEntry.installment_number}/${selectedEntry.total_installments}`
    : null;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    currency: 'TRY',
    type: 'expense',
    category: 'other',
    account_name: '',
    account_type: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_day: 1,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with selected entry data
  useEffect(() => {
    if (selectedEntry) {
      setFormData({
        title: selectedEntry.title || '',
        description: selectedEntry.description || '',
        amount: selectedEntry.amount?.toString() || '',
        currency: selectedEntry.currency || 'TRY',
        type: selectedEntry.type || 'expense',
        category: selectedEntry.category || 'other',
        account_name: selectedEntry.account_name || '',
        account_type: selectedEntry.account_type || '',
        date: selectedEntry.date || format(new Date(), 'yyyy-MM-dd'),
        is_recurring: selectedEntry.is_recurring || false,
        recurring_frequency: selectedEntry.recurring_rule?.frequency || 'monthly',
        recurring_day: selectedEntry.recurring_rule?.dayOfMonth || selectedEntry.recurring_rule?.dayOfWeek || 1,
      });
    } else {
      // Default to current month's first day for new entries
      setFormData(prev => ({
        ...prev,
        date: format(currentMonth, 'yyyy-MM-dd'),
      }));
    }
  }, [selectedEntry, currentMonth]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Başlık gerekli');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Geçerli bir tutar girin');
      return;
    }
    if (!formData.date) {
      setError('Tarih gerekli');
      return;
    }

    setIsSubmitting(true);

    try {
      const entryData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        type: formData.type,
        category: formData.category,
        account_name: formData.account_name.trim() || null,
        account_type: formData.account_type || null,
        date: formData.date,
        is_recurring: formData.is_recurring,
        recurring_rule: formData.is_recurring
          ? {
              frequency: formData.recurring_frequency,
              ...(formData.recurring_frequency === 'monthly' || formData.recurring_frequency === 'yearly'
                ? { dayOfMonth: parseInt(formData.recurring_day) }
                : { dayOfWeek: parseInt(formData.recurring_day) }),
              startDate: formData.date,
              endDate: null,
            }
          : null,
      };

      if (isEditing) {
        await updateEntry(selectedEntry.id, entryData);

        // If this is a loan installment and shared fields changed, update all in group
        if (isLoanInstallment) {
          const sharedFieldsChanged =
            formData.account_name !== (selectedEntry.account_name || '') ||
            formData.account_type !== (selectedEntry.account_type || '');

          if (sharedFieldsChanged) {
            await updateLoanGroupSharedFields(selectedEntry.loan_group_id, {
              account_name: formData.account_name.trim() || null,
              account_type: formData.account_type || null,
            });
          }
        }
      } else {
        await createEntry(entryData);
      }
    } catch (err) {
      setError(err.message || 'Bir hata oluştu');
      setIsSubmitting(false);
    }
  };

  const [showDeleteOptions, setShowDeleteOptions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteAll, setPendingDeleteAll] = useState(false);

  const handleDelete = async (deleteAll = false) => {
    if (!isEditing) return;

    // For loan installments, show options first
    if (isLoanInstallment && !showDeleteOptions) {
      setShowDeleteOptions(true);
      return;
    }

    // Show confirmation dialog
    if (!showDeleteConfirm) {
      setPendingDeleteAll(deleteAll);
      setShowDeleteConfirm(true);
      return;
    }

    // User confirmed, proceed with deletion
    setIsSubmitting(true);
    try {
      if (deleteAll && isLoanInstallment) {
        await deleteLoanGroup(selectedEntry.loan_group_id);
      } else {
        await deleteEntry(selectedEntry.id);
      }
    } catch (err) {
      setError(err.message || 'Silme işlemi başarısız');
      setIsSubmitting(false);
    }
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    handleDelete(pendingDeleteAll);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setShowDeleteOptions(false);
    setPendingDeleteAll(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-xl shadow-2xl my-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {isEditing ? 'Girişi Düzenle' : 'Yeni Giriş'}
              </h2>
              <p className="text-sm text-gray-400">
                {isEditing ? 'Değişiklikleri kaydedin' : 'Manuel giriş oluşturun'}
              </p>
            </div>
          </div>
          <button
            onClick={closeEditModal}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Başlık *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Örn: Maaş, Netflix, Kira"
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Amount & Currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Tutar *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Para Birimi
              </label>
              <select
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                disabled={isSubmitting}
              >
                {CURRENCIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Tip
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleChange('type', 'income')}
                className={`flex-1 py-2.5 rounded-lg border transition-colors ${
                  formData.type === 'income'
                    ? 'bg-emerald-900/30 border-emerald-500 text-emerald-400'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
                disabled={isSubmitting}
              >
                Gelir
              </button>
              <button
                type="button"
                onClick={() => handleChange('type', 'expense')}
                className={`flex-1 py-2.5 rounded-lg border transition-colors ${
                  formData.type === 'expense'
                    ? 'bg-red-900/30 border-red-500 text-red-400'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
                disabled={isSubmitting}
              >
                Gider
              </button>
            </div>
          </div>

          {/* Category & Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                <Tag className="inline w-4 h-4 mr-1" />
                Kategori
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                disabled={isSubmitting}
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                <Calendar className="inline w-4 h-4 mr-1" />
                Tarih *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Account */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                <Building2 className="inline w-4 h-4 mr-1" />
                Hesap Adı
              </label>
              <input
                type="text"
                value={formData.account_name}
                onChange={(e) => handleChange('account_name', e.target.value)}
                placeholder="Örn: Enpara, Yapıkredi"
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Hesap Tipi
              </label>
              <select
                value={formData.account_type}
                onChange={(e) => handleChange('account_type', e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                disabled={isSubmitting}
              >
                <option value="">Seçiniz</option>
                {ACCOUNT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Recurring */}
          <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => handleChange('is_recurring', e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-gray-900"
                disabled={isSubmitting}
              />
              <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300">Tekrarlayan</span>
              </div>
            </label>

            {formData.is_recurring && (
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-700">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Sıklık</label>
                  <select
                    value={formData.recurring_frequency}
                    onChange={(e) => handleChange('recurring_frequency', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                    disabled={isSubmitting}
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    {formData.recurring_frequency === 'monthly' || formData.recurring_frequency === 'yearly'
                      ? 'Ayın Günü'
                      : 'Haftanın Günü'}
                  </label>
                  {formData.recurring_frequency === 'monthly' || formData.recurring_frequency === 'yearly' ? (
                    <select
                      value={formData.recurring_day}
                      onChange={(e) => handleChange('recurring_day', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      disabled={isSubmitting}
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={formData.recurring_day}
                      onChange={(e) => handleChange('recurring_day', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                      disabled={isSubmitting}
                    >
                      <option value="1">Pazartesi</option>
                      <option value="2">Salı</option>
                      <option value="3">Çarşamba</option>
                      <option value="4">Perşembe</option>
                      <option value="5">Cuma</option>
                      <option value="6">Cumartesi</option>
                      <option value="0">Pazar</option>
                    </select>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Açıklama
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Opsiyonel notlar..."
              rows={2}
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Loan installment info */}
          {isLoanInstallment && (
            <div className="p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-300">
                  <Tag className="w-4 h-4" />
                  <span className="font-medium">{installmentInfo}</span>
                </div>
                {selectedEntry.creditor && (
                  <span className="text-sm text-gray-400">{selectedEntry.creditor}</span>
                )}
              </div>
              {selectedEntry.principal_amount && (
                <div className="flex gap-4 mt-2 text-sm text-gray-400">
                  <span>Anapara: {selectedEntry.principal_amount?.toLocaleString('tr-TR')} {formData.currency}</span>
                  {selectedEntry.interest_amount && (
                    <span>Faiz: {selectedEntry.interest_amount?.toLocaleString('tr-TR')} {formData.currency}</span>
                  )}
                </div>
              )}
              <p className="text-xs text-blue-400/70 mt-2">
                Hesap bilgileri değiştirildiğinde tüm taksitlere uygulanır
              </p>
            </div>
          )}

          {/* Delete options for loan installments */}
          {showDeleteOptions && isLoanInstallment && !showDeleteConfirm && (
            <div className="p-3 bg-red-900/20 border border-red-700/30 rounded-lg space-y-2">
              <p className="text-sm text-red-300">Bu giriş bir kredi taksitine ait. Ne yapmak istiyorsunuz?</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleDelete(false)}
                  disabled={isSubmitting}
                  className="flex-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  Sadece Bu Taksiti Sil
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(true)}
                  disabled={isSubmitting}
                  className="flex-1 px-3 py-2 bg-red-900/50 hover:bg-red-900/70 text-red-300 rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                  Tüm Taksitleri Sil ({selectedEntry.total_installments})
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteOptions(false)}
                className="w-full text-xs text-gray-500 hover:text-gray-400"
              >
                İptal
              </button>
            </div>
          )}

          {/* Delete confirmation dialog */}
          {showDeleteConfirm && (
            <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-red-300">Silmek istediğinize emin misiniz?</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {pendingDeleteAll && isLoanInstallment
                      ? `"${selectedEntry.title}" ve ilişkili ${selectedEntry.total_installments} taksit kalıcı olarak silinecek.`
                      : `"${selectedEntry.title}" kalıcı olarak silinecek. Bu işlem geri alınamaz.`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {isSubmitting ? 'Siliniyor...' : 'Evet, Sil'}
                </button>
              </div>
            </div>
          )}

          {/* Actions - hide when delete confirm is showing */}
          {!showDeleteConfirm && (
            <div className="flex gap-3 pt-2">
              {isEditing && !showDeleteOptions && (
                <button
                  type="button"
                  onClick={() => handleDelete()}
                  disabled={isSubmitting}
                  className="px-4 py-2.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={closeEditModal}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          )}
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EditEntryModal;
